import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/home.css";
import cscscard from "../images/cscscard.png";

const Home = () => {
  // --- Dynamic Hero Text Logic ---
  const roles = [
    "Labourers",
    "General Operatives",
    "Site Workers",
    "Construction Workers",
    "Trainees",
  ];
  const [currentRole, setCurrentRole] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const roleInterval = setInterval(() => {
      setFade(false); // Start fade out
      setTimeout(() => {
        setCurrentRole((prev) => (prev + 1) % roles.length);
        setFade(true); // Start fade in
      }, 500);
    }, 3500);

    return () => clearInterval(roleInterval);
  }, [roles.length]);

  // --- Contact Form Logic ---
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch("https://getyourcscs.co.uk/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: "success",
          message: "Message sent! We will be in touch shortly.",
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        setStatus({
          type: "error",
          message: "Something went wrong. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus({
        type: "error",
        message:
          "Failed to connect to the server. Please check your connection.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper flat-dark-theme">
      {/* --- SEO METADATA START --- */}
      <Helmet>
        <title>Get Your Green CSCS Card | Home</title>
        <meta
          name="description"
          content="Get your Green CSCS Card and pass your HS&E test in just 1 day. All-inclusive £400 training courses at our CSCS UK approved Walsall and West Bromwich centres."
        />
        <meta
          name="keywords"
          content="Green CSCS card, labourer card, HS&E test, construction training West Midlands, get your CSCS, CSCS course Walsall, CSCS test West Bromwich, Level 1 Health and Safety"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getyourcscs.co.uk/" />
        <meta property="og:title" content="Get Your Green CSCS Card | Home" />
        <meta
          property="og:description"
          content="Fast-track your construction career. 1-Day Green CSCS training and HS&E testing with a 98% pass rate."
        />
        <link rel="canonical" href="https://www.getyourcscs.co.uk/" />
      </Helmet>

      {/* 1. Dynamic Hero Section */}
      {/* 1. Dynamic Hero Section */}
      <section className="hero-section">
        {/* Directional gradient overlay to darken the left side only */}
        <div className="hero-bg-overlay"></div>

        <div className="section-container hero-container relative-z">
          {/* Left Side: Glassmorphic Text Content */}
          <div className="glass-panel hero-content-panel floating-element-subtle">
            <h1 className="hero-main-title">
              CSCS UK Approved Training & Green Cards for{" "}
              <span className={`dynamic-role ${fade ? "fade-in" : "fade-out"}`}>
                {roles[currentRole]}
              </span>
            </h1>
            <p className="hero-subtitle">
              Complete your Level 1 Health, Safety and Environment course and
              HS&E test in just 1 day at our approved West Midlands centres.
            </p>

            {/* Package Info Inline */}
            <div className="info-box-styled glass-inner-box mb-4">
              <div className="info-row-styled info-row-border">
                <span className="text-muted">Training Course</span>
                <strong className="text-light">Level 1 H&S</strong>
              </div>
              <div className="info-row-styled">
                <span className="text-muted">Required Test</span>
                <strong className="text-light">CITB HS&E</strong>
              </div>
            </div>

            <div className="hero-actions">
              <Link to="/booking" className="btn-primary pulse-hover">
                View Available Slots
              </Link>
              <Link to="/about" className="btn-secondary">
                View Course Details
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* 2. Trust / Stats Bar */}
      <div className="trust-bar bg-alt">
        <div className="section-container trust-grid">
          <div className="trust-item">
            <strong>98%</strong> Pass Rate
          </div>
          <div className="trust-item">
            <strong>Same-Day</strong> Test Results
          </div>
          <div className="trust-item">
            <strong>CSCS UK</strong> Approved Centre
          </div>
          <div className="trust-item">
            <strong>5-Star</strong> Rated Training
          </div>
        </div>
      </div>

      {/* 3. How It Works Section */}
      <section className="steps-section">
        <div className="section-container">
          <h2 className="section-title">How to get your Green CSCS Card</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Book Your Slot</h3>
              <p>
                Choose a date for your 1-day course and test at our Walsall or
                West Bromwich centre.
              </p>
            </div>
            <div className="step-line"></div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Attend Training</h3>
              <p>
                Join our classroom session. We provide all the materials and
                expert instruction you need to pass.
              </p>
            </div>
            <div className="step-line"></div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Pass & Receive</h3>
              <p>
                Take your computer-based HS&E test in the afternoon. Pass, and
                we apply for your Green card immediately.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Features Section */}
      <section className="features-section bg-alt">
        <div className="section-container">
          <div className="features-grid">
            <div className="feature-card flat-panel">
              <span className="feature-icon">⏱️</span>
              <h3>1-Day Turnaround</h3>
              <p>
                Complete both your classroom training and computer-based HS&E
                test on the exact same day.
              </p>
            </div>
            <div className="feature-card flat-panel">
              <span className="feature-icon">👨‍🏫</span>
              <h3>Expert Assessors</h3>
              <p>
                Learn face-to-face with qualified construction industry
                veterans, not just an online slide deck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Quick Info / Card Preview Section */}
      <section className="info-preview-section">
        <div className="section-container">
          <div className="info-grid">
            <div className="info-text">
              <h2>Get Your Green Labourer Card</h2>
              <p>
                The <strong>Green General Operative Card</strong> is the
                standard requirement to access most UK construction sites as a
                labourer. We provide the comprehensive Level 1 Health & Safety
                course and facilitate the exact HS&E Operatives test you need to
                get on site.
              </p>

              <ul className="location-list spaced-list">
                <li>
                  <strong>📍 Our Training Centre</strong>
                  <span>86b High Street, West Bromwich, B70 6JW</span>
                </li>
              </ul>

              <Link to="/about" className="text-link">
                View Green Card requirements →
              </Link>
            </div>

            {/* Card Image Showcase Panel */}
            <div className="flat-panel hero-card-panel">
              <img
                src={cscscard}
                alt="Green Labourer CSCS Card"
                className="preview-card-image"
              />
              <h3 className="preview-title">Green CSCS Card</h3>
              <p className="preview-subtitle">Labourer / General Operative</p>

              {/* TAILWIND CENTERING APPLIED HERE */}
              <div className="info-box-styled preview-box flex flex-col items-center justify-center text-center space-y-3">
                <p className="preview-box-text flex flex-col items-center">
                  <strong className="preview-box-label">Required Test</strong>
                  HS&E Test
                </p>
                <p className="preview-box-text mb-0 flex flex-col items-center">
                  <strong className="preview-box-label">Validity</strong>2 Years
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FAQ Section */}
      <section className="faq-section bg-alt">
        <div className="section-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item flat-panel">
              <h4>What do I need to bring on the day?</h4>
              <p>
                You MUST bring a valid UK Driving Licence or Passport. Without
                this photographic ID, you cannot sit the test and will not
                receive a refund.
              </p>
            </div>
            <div className="faq-item flat-panel">
              <h4>What if I fail the test?</h4>
              <p>
                If you do not pass the HS&E test on your first attempt, you will
                need to rebook the test. Speak to our instructors on the day for
                advice on re-sits.
              </p>
            </div>
            <div className="faq-item flat-panel">
              <h4>How long does the card take to arrive?</h4>
              <p>
                Once you pass and your application is submitted, your physical
                Green CSCS card will arrive in 10 working days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Contact Form Section */}
      <section className="home-contact-section">
        <div className="section-container form-container">
          <div className="contact-header">
            <h2>Have a question?</h2>
            <p>
              Drop us a message below and our team will get back to you today.
            </p>
          </div>

          <form onSubmit={handleFormSubmit} className="flat-form">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="07123 456789"
              />
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                name="message"
                required
                value={formData.message}
                onChange={handleInputChange}
                placeholder="How can we help you?"
                rows="4"
              ></textarea>
            </div>

            {status.message && (
              <div className={`status-message ${status.type}`}>
                {status.message}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
